import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useHeaderVisibility } from '@/hooks/useHeaderVisibility';
import { useIsMobile } from '@/hooks/use-mobile';
import { HEADER_H } from '@/components/layout/StickyToolbar';

/**
 * The header is `position: fixed`, and each page renders it (plus its spacer) itself.
 * Anything a layout wants "at the top of the page" must therefore render *after* the
 * spacer, or it sits hidden underneath the header. ProtectedRoute decides WHAT goes
 * here (Desk Setup ribbon, push prompt); AppHeader decides WHERE (right below itself).
 *
 * On tablet/desktop the strip stays docked below the header while scrolling and
 * publishes its height as --desk-ribbon-h so search toolbars dock beneath it.
 * On phones it scrolls away to keep the screen for listings.
 */
const BelowHeaderSlotContext = createContext<ReactNode>(null);

export const BelowHeaderSlotProvider = BelowHeaderSlotContext.Provider;

export function BelowHeaderSlot() {
  const content = useContext(BelowHeaderSlotContext);
  const headerVisible = useHeaderVisibility();
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const sticky = !!content && !isMobile;

  useEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!sticky || !el) {
      root.style.setProperty('--desk-ribbon-h', '0px');
      return;
    }
    const publish = () => root.style.setProperty('--desk-ribbon-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--desk-ribbon-h', '0px');
    };
  }, [sticky]);

  if (!content) return null;
  return (
    <div
      ref={ref}
      data-below-header-slot
      style={sticky ? { top: headerVisible ? HEADER_H : 0 } : undefined}
      className={sticky ? 'sticky z-30 bg-background transition-[top] duration-300' : undefined}
    >
      <div className="container-wide px-4 sm:px-6 lg:px-8 py-2 space-y-2 empty:hidden">{content}</div>
    </div>
  );
}
